import { Button, Flex, Select, SimpleGrid, TextInput } from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import omit from "lodash/omit";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";

import { useCompanyOptions } from "../../api/hooks/customer/useCompanyOptions.ts";
import { useLimitDetails } from "../../api/hooks/limit/useLimitDetails.ts";
import { useCreatePaymaster } from "../../api/hooks/paymaster/useCreatePaymaster.ts";
import { useDeletePaymaster } from "../../api/hooks/paymaster/useDeletePaymaster.ts";
import { usePaymasters } from "../../api/hooks/paymaster/usePaymasters.ts";
import { useUpdatePaymaster } from "../../api/hooks/paymaster/useUpdatePaymaster.ts";
import { useUpdatePaymasterNotes } from "../../api/hooks/paymaster/useUpdatePaymasterNotes.ts";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal.tsx";
import SvgPlus from "../../components/Icons/Plus.tsx";
import Layout from "../../components/Layout/Layout.tsx";
import { MultiSelect } from "../../components/MultiSelect.tsx";
import Table from "../../components/Table/Table.tsx";
import { SortDirection } from "../../components/Table/types.ts";
import Title from "../../components/Title/Title.tsx";
import {
  CreatePaymasterInput,
  LimitRequestType,
  OrderBy,
  PaymasterOrderBy,
  RolePermission,
  UpdatePaymasterInput,
  UpdatePaymasterNotesInput,
} from "../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../hooks/useMutationNotificationWrapper.tsx";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { Paymaster, paymasterScoreOptions } from "../../types/paymaster.ts";
import { getApiOrderBy } from "../../utils/api.ts";
import { getCompanyOptions } from "../../utils/company.ts";
import { getPagesCount } from "../../utils/pagination.ts";
import { hasPermission } from "../../utils/user.ts";
import { CreatePaymasterModal } from "./CreatePaymasterModal/CreatePaymasterModal.tsx";
import { usePaymasterTableData } from "./hooks/usePaymasterTableData.tsx";
import { PaymasterCustomersModal } from "./PaymasterCustomersModal.tsx";
import { PaymasterInvoicesModal } from "./PaymasterInvoicesModal.tsx";
import { PaymasterFiltersValue } from "./types.ts";
import { getPaymasterApiFilter } from "./utils.ts";

const PaymasterAnalysisPage = () => {
  const { user } = useCurrentUserContext();

  const canEdit = hasPermission(user, RolePermission.CanEditPaymaster);

  const [selectedPaymaster, setSelectedPaymaster] = useState<Paymaster>();

  const [page, setPage] = useState<number>(1);
  const [orderBy, setOrderBy] = useState<PaymasterOrderBy>({
    id: OrderBy.Desc,
  });
  const [filter, setFilter] = useState<PaymasterFiltersValue>({});
  const [paymasterName, setPaymasterName] = useDebouncedState("", 500);
  const apiFilter = getPaymasterApiFilter({ ...filter, name: paymasterName });

  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [
    customersModalOpened,
    { open: openCustomersModal, close: closeCustomersModal },
  ] = useDisclosure(false);
  const [
    invoicesModalOpened,
    { open: openInvoicesModal, close: closeInvoicesModal },
  ] = useDisclosure(false);
  const [
    deleteConfirmationModalOpened,
    { open: openDeleteConfirmationModal, close: closeDeleteConfirmationModal },
  ] = useDisclosure(false);

  const [{ data, fetching: paymastersFetching }, refresh] = usePaymasters({
    filter: apiFilter,
    orderBy,
    page,
  });
  const paymasters = data?.paymasters?.data;

  const pageCount = getPagesCount(data?.paymasters?.total);

  const [{ data: customerOptionsData, fetching: customerOptionsFetching }] =
    useCompanyOptions();
  const customerOptions = customerOptionsData?.customers.data;
  const [{ data: limitDetailsData, fetching: limitDetailsFetching }] =
    useLimitDetails({});
  const limitDetails = (limitDetailsData?.limitDetails.data ?? []).filter(
    (ld) =>
      !ld.limitRequest ||
      ld.limitRequest.limitRequestType !== LimitRequestType.TopUpLimit
  );

  const [, createPaymaster] = useMutationNotificationWrapper(
    useCreatePaymaster(),
    {
      success: {
        message: "Paymaster has been successfully created.",
      },
    }
  );

  const [, updatePaymaster] = useMutationNotificationWrapper(
    useUpdatePaymaster(),
    {
      success: {
        message: "Paymaster has been successfully updated.",
      },
    }
  );
  const [, updatePaymasterNotes] = useMutationNotificationWrapper(
    useUpdatePaymasterNotes(),
    {
      success: {
        message: "Paymaster has been successfully updated.",
      },
    }
  );

  const [, deletePaymaster] = useMutationNotificationWrapper(
    useDeletePaymaster(),
    {
      success: {
        message: "Paymaster has been successfully deleted.",
      },
    }
  );

  const handleUpdatePaymasterNotes = useCallback(
    (id: number, input: UpdatePaymasterNotesInput) => {
      updatePaymasterNotes({ id, input }).then(refresh);
    },
    [refresh, updatePaymasterNotes]
  );

  const handleOpenDeleteConfirmationModal = useCallback(
    (id: number) => {
      const paymaster = paymasters?.find((paymaster) => paymaster.id === id);
      if (!paymaster) {
        return;
      }
      setSelectedPaymaster(paymaster);
      openDeleteConfirmationModal();
    },
    [openDeleteConfirmationModal, paymasters]
  );

  const handleOpenEditModal = useCallback(
    (id: number) => {
      const paymaster = paymasters?.find((paymaster) => paymaster.id === id);
      if (!paymaster) {
        return;
      }
      setSelectedPaymaster(paymaster);
      openCreateModal();
    },
    [openCreateModal, paymasters]
  );

  const handleOpenCustomersModal = useCallback(
    (id: number) => {
      const paymaster = paymasters?.find((paymaster) => paymaster.id === id);
      if (!paymaster) {
        return;
      }
      setSelectedPaymaster(paymaster);
      openCustomersModal();
    },
    [openCustomersModal, paymasters]
  );

  const handleOpenInvoicesModal = useCallback(
    (id: number) => {
      const paymaster = paymasters?.find((paymaster) => paymaster.id === id);
      if (!paymaster) {
        return;
      }
      setSelectedPaymaster(paymaster);
      openInvoicesModal();
    },
    [openInvoicesModal, paymasters]
  );

  const handleCloseEditModal = useCallback(() => {
    setSelectedPaymaster(undefined);
    closeCreateModal();
  }, [closeCreateModal]);

  const handleCloseCustomersModal = useCallback(() => {
    setSelectedPaymaster(undefined);
    closeCustomersModal();
  }, [closeCustomersModal]);

  const handleCloseInvoicesModal = useCallback(() => {
    setSelectedPaymaster(undefined);
    closeInvoicesModal();
  }, [closeInvoicesModal]);

  const [columns, rows] = usePaymasterTableData({
    data: paymasters,
    onInlineEdit: handleUpdatePaymasterNotes,
    onEdit: handleOpenEditModal,
    onDelete: handleOpenDeleteConfirmationModal,
    onOpenCustomers: handleOpenCustomersModal,
    onOpenInvoices: handleOpenInvoicesModal,
  });

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getApiOrderBy(sort));
    },
    [setOrderBy]
  );

  const handleFilterChange = useCallback(
    (value: Partial<PaymasterFiltersValue>) => {
      setFilter((prevFilter) => ({
        ...prevFilter,
        ...value,
      }));
    },
    [setFilter]
  );

  const handleCreatePaymaster = useCallback(
    (input: UpdatePaymasterInput) => {
      const promise = selectedPaymaster
        ? updatePaymaster({
            id: selectedPaymaster.id,
            input: input as UpdatePaymasterInput,
          })
        : createPaymaster({
            input: {
              ...input,
              customers: input.customers?.map((c) => omit(c, "id")),
            } as CreatePaymasterInput,
          });
      promise.then(handleCloseEditModal).finally(refresh);
    },
    [
      createPaymaster,
      handleCloseEditModal,
      refresh,
      selectedPaymaster,
      updatePaymaster,
    ]
  );

  const handleDeletePaymaster = useCallback(() => {
    if (!selectedPaymaster) {
      closeDeleteConfirmationModal();
      return;
    }
    deletePaymaster({ id: selectedPaymaster.id })
      .then(refresh)
      .finally(() => {
        setSelectedPaymaster(undefined);
        closeDeleteConfirmationModal();
      });
  }, [
    closeDeleteConfirmationModal,
    deletePaymaster,
    refresh,
    selectedPaymaster,
  ]);

  return (
    <>
      <Layout>
        <Flex direction="column" gap={{ base: 16, sm: 24 }}>
          <Flex direction="column" gap={16}>
            <Title size="h1">Paymaster Analysis</Title>
            {canEdit && (
              <Button
                uppercase
                variant="filledGolden"
                leftIcon={<SvgPlus />}
                loading={customerOptionsFetching || limitDetailsFetching}
                onClick={openCreateModal}
              >
                Add
              </Button>
            )}
            <SimpleGrid
              w="100%"
              cols={4}
              breakpoints={[
                { maxWidth: "sm", cols: 2 },
                { maxWidth: "lg", cols: 3 },
              ]}
              spacing={20}
              verticalSpacing={12}
            >
              <MultiSelect
                loading={customerOptionsFetching}
                searchable
                clearable
                size="m"
                placeholder="Customer Name"
                data={getCompanyOptions(customerOptions)}
                onChange={(customerId) => handleFilterChange({ customerId })}
              />
              <TextInput
                size="m"
                placeholder="Paymaster Name"
                onChange={(event) => {
                  setPaymasterName(event.target.value);
                }}
              />
              <Select
                placeholder="Score"
                size="m"
                clearable
                data={paymasterScoreOptions}
                onChange={(score) => handleFilterChange({ score })}
              />
            </SimpleGrid>
          </Flex>

          <Table
            loading={paymastersFetching}
            columns={columns}
            rows={rows}
            verticalAlignment="center"
            defaultSort={{
              columnAccessor: "id",
              direction: SortDirection.desc,
            }}
            pagination={{
              pageCount: pageCount,
              page: page,
              onPageChange: handlePageChange,
            }}
            onSortChange={handleSortChange}
          />
        </Flex>
        {createModalOpened && (
          <CreatePaymasterModal
            data={selectedPaymaster}
            customers={customerOptions}
            limitDetails={limitDetails}
            opened={true}
            onClose={handleCloseEditModal}
            onSubmit={handleCreatePaymaster}
          />
        )}
        {customersModalOpened && selectedPaymaster && (
          <PaymasterCustomersModal
            paymaster={selectedPaymaster}
            opened={true}
            onClose={handleCloseCustomersModal}
          />
        )}
        {invoicesModalOpened && selectedPaymaster && (
          <PaymasterInvoicesModal
            paymaster={selectedPaymaster}
            opened={true}
            onClose={handleCloseInvoicesModal}
          />
        )}
        {deleteConfirmationModalOpened && (
          <ConfirmationModal
            opened={true}
            message={"Are you sure you want to delete this data?"}
            onConfirm={handleDeletePaymaster}
            onClose={closeDeleteConfirmationModal}
          />
        )}
      </Layout>
    </>
  );
};

export default PaymasterAnalysisPage;
